import { Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import LogoIcon from './ui/LogoIcon';
import { cn, formatRelativeTime } from '@/utils/utils';
import { getSession, logout } from '@/services/auth';
import {
  getNotifications,
  markNotificationRead,
  type AppNotification,
} from '@/services/notifications';

type Crumb = { name: string; href?: string; active?: boolean };

const PATH_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  users: 'Users',
  goals: 'Goals',
  activity: 'Activity',
  groups: 'Groups',
  revenue: 'Revenue',
  notifications: 'Notification',
  'email-templates': 'Email Template',
  'email-logs': 'Email Log',
  all: 'All Notifications',
  settings: 'Settings',
  permissions: 'Permissions',
  'report-log': 'Report Log',
};

function buildBreadcrumbs(pathname: string): Crumb[] {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [];
  let acc = '';
  parts.forEach((part, i) => {
    acc += `/${part}`;
    const last = i === parts.length - 1;
    crumbs.push({
      name: PATH_LABELS[part] ?? part.charAt(0).toUpperCase() + part.slice(1),
      href: last ? undefined : acc,
      active: last,
    });
  });
  return crumbs;
}

interface TopbarProps {
  onMenuClick?: () => void;
}

export default function Topbar({ onMenuClick }: TopbarProps) {
  const { pathname } = useLocation();
  const breadcrumbs = useMemo(() => buildBreadcrumbs(pathname), [pathname]);

  const session = getSession();
  const userName = session?.user_name || 'Admin';
  const displayName = userName.split(' ').slice(0, 2).join(' ');
  const initials = userName
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const [notifOpen, setNotifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifError, setNotifError] = useState('');
  const [markingAll, setMarkingAll] = useState(false);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node))
        setNotifOpen(false);
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
        setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getNotifications()
      .then((res) => {
        if (!cancelled) setNotifications(res.notifications);
      })
      .catch((err) => {
        if (!cancelled) {
          setNotifError(err instanceof Error ? err.message : 'Failed to load notifications');
        }
      })
      .finally(() => {
        if (!cancelled) setNotifLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const markAllRead = async () => {
    const unread = notifications.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    setMarkingAll(true);
    try {
      await Promise.all(unread.map((n) => markNotificationRead(n.id)));
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {
      /* leave state as-is; user can retry */
    } finally {
      setMarkingAll(false);
    }
  };

  const markOneRead = async (id: number) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    try {
      await markNotificationRead(id);
    } catch {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: false } : n))
      );
    }
  };

  return (
    <header className="sticky top-4 z-20 mx-4 mt-4 flex h-16 items-center justify-between rounded-2xl bg-white/90 px-4 shadow-sm ring-1 ring-orange-100 backdrop-blur-md sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open menu"
          className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-stone-500 transition hover:bg-orange-50 hover:text-orange-600 lg:hidden"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
            <path strokeWidth="2" strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div className="flex items-center gap-2 lg:hidden">
          <LogoIcon className="h-7 w-7" />
          <span className="text-sm font-extrabold text-stone-800">Lifebit</span>
        </div>

        <nav className="hidden min-w-0 items-center gap-2 text-sm transition-all duration-300 sm:flex">
          <Link
            to="/dashboard"
            className="text-stone-400 transition hover:text-orange-500"
            aria-label="Home"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="h-4 w-4"
            >
              <path
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 10.5 12 3l9 7.5M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9"
              />
            </svg>
          </Link>

          {breadcrumbs.map((item, index) => (
            <span key={index} className="flex items-center gap-2 truncate">
              <span className="text-stone-300">/</span>
              {item.href ? (
                <Link
                  to={item.href}
                  className="truncate text-stone-400 transition hover:text-orange-500"
                >
                  {item.name}
                </Link>
              ) : (
                <span
                  className={cn(
                    'truncate font-semibold',
                    item.active ? 'text-stone-700' : 'text-stone-400'
                  )}
                >
                  {item.name}
                </span>
              )}
            </span>
          ))}
        </nav>
      </div>

      <div className="flex items-center gap-3">
        <label className="relative hidden md:block">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-stone-400">
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
              <path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 001.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 00-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.471 6.471 0 005.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
          </span>
          <input
            type="search"
            placeholder="Search…"
            className="w-44 rounded-full border border-orange-100 bg-orange-50/50 pl-9 pr-4 py-2 text-sm placeholder:text-stone-400 transition-colors focus:w-64 focus:border-orange-300 focus:bg-white focus:outline-none"
          />
        </label>

        <div className="relative" ref={notifRef}>
          <button
            type="button"
            aria-label="Notifications"
            className={cn(
              'inline-flex h-10 w-10 items-center justify-center rounded-full border border-orange-100 bg-white text-stone-600 shadow-sm transition hover:bg-orange-50 hover:text-orange-600 active:scale-[0.99]',
              notifOpen && 'ring-2 ring-orange-200'
            )}
            onClick={() => setNotifOpen((p) => !p)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0 1 18 14.158V11a6.002 6.002 0 0 0-4-5.659V5a2 2 0 1 0-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9"
              />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white shadow">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
                <span className="relative">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-12 z-50 w-80 overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-xl sm:w-96">
              <div className="flex items-center justify-between border-b border-orange-50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-stone-700">
                    Notifications
                  </span>
                  {unreadCount > 0 && (
                    <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-600">
                      {unreadCount} unread
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    disabled={markingAll}
                    className="rounded-lg px-2 py-1 text-[11px] font-medium text-orange-600 transition hover:bg-orange-50 disabled:opacity-50"
                  >
                    {markingAll ? 'Marking…' : 'Mark all read'}
                  </button>
                )}
              </div>

              <div className="max-h-80 overflow-y-auto">
                {notifLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-6 w-6 animate-spin rounded-full border-4 border-orange-200 border-t-orange-500" />
                  </div>
                ) : notifError ? (
                  <div className="px-4 py-6 text-center text-sm text-rose-600">
                    {notifError}
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-8">
                    <p className="text-sm font-medium text-stone-500">
                      All caught up!
                    </p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={cn(
                        'border-b border-orange-50 px-4 py-3 transition last:border-b-0',
                        notif.is_read ? 'bg-white' : 'bg-orange-50/60'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p
                            className={cn(
                              'truncate text-sm',
                              notif.is_read
                                ? 'text-stone-600'
                                : 'font-semibold text-stone-800'
                            )}
                          >
                            {notif.title}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-xs text-stone-500">
                            {notif.body}
                          </p>
                          <p className="mt-1 text-[10px] text-stone-400">
                            {formatRelativeTime(notif.created_at)}
                          </p>
                        </div>
                        {!notif.is_read && (
                          <button
                            type="button"
                            title="Mark as read"
                            onClick={() => markOneRead(notif.id)}
                            className="mt-0.5 shrink-0 rounded-lg px-2 py-1 text-[11px] font-medium text-orange-600 transition hover:bg-orange-50"
                          >
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="border-t border-orange-50">
                <Link
                  to="/dashboard/notifications/all"
                  onClick={() => setNotifOpen(false)}
                  className="flex w-full items-center justify-center py-2.5 text-xs font-semibold text-orange-600 transition hover:bg-orange-50"
                >
                  Show all notifications →
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="relative flex items-center gap-3" ref={menuRef}>
          <span className="hidden text-sm font-semibold text-stone-700 sm:inline-flex">
            {displayName}
          </span>
          <button
            type="button"
            aria-label="Open user menu"
            className={cn(
              'inline-flex h-10 w-10 items-center justify-center rounded-full border border-orange-100 bg-gradient-to-br from-orange-400 to-orange-600 text-sm font-bold text-white shadow-sm transition hover:shadow-md active:scale-[0.99]',
              menuOpen && 'ring-2 ring-orange-200'
            )}
            onClick={() => setMenuOpen((p) => !p)}
          >
            {initials}
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-12 z-50 w-56 overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-xl">
              <div className="border-b border-orange-50 px-4 py-3">
                <p className="text-sm font-semibold text-stone-700">{displayName}</p>
                <p className="text-xs text-stone-500">{session?.role ?? 'admin'}</p>
              </div>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-3 text-sm text-stone-600 transition hover:bg-orange-50"
                onClick={() => setMenuOpen(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4 text-orange-500"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                View account
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-3 text-sm text-rose-600 transition hover:bg-rose-50"
                onClick={() => {
                  setMenuOpen(false);
                  void logout().finally(() => {
                    window.location.href = '/login';
                  });
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
